import { format } from "date-fns";

export const NEW_APPOINTMENT_VALUE = "0";

export const isNil = (value) => value === undefined || value === null;

export const isBlank = (value) =>
  isNil(value) || (typeof value === "string" && value.trim() === "");

export const toOption = (value) => {
  if (isNil(value) || value === "") return null;
  if (typeof value === "object" && value.value !== undefined) return value;
  if (typeof value === "object" && value.id !== undefined) {
    return { value: value.id, label: value.label ?? value.name ?? String(value.id) };
  }
  return { value, label: String(value) };
};

export const pickValue = (value) => {
  if (isNil(value)) return "";
  if (typeof value === "object") return value?.value ?? value?.id ?? "";
  return value;
};

export const pickStringValue = (value) => {
  const resolved = pickValue(value);
  return isNil(resolved) ? "" : String(resolved);
};

export const normalizePanamaPhone = (value) => {
  const rawValue =
    typeof value === "string" ? value.trim() : String(value ?? "").trim();

  if (!rawValue) {
    return "";
  }

  const digitsOnly = rawValue.replace(/\D/g, "");

  if (rawValue.startsWith("+")) {
    return digitsOnly ? `+${digitsOnly}` : "";
  }

  if (!digitsOnly) {
    return rawValue;
  }

  if (digitsOnly.startsWith("507")) {
    return `+${digitsOnly}`;
  }

  return `+507${digitsOnly}`;
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const normalizeDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    const dateOnlyMatch = trimmed.match(DATE_ONLY_PATTERN);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatBirthDate = (value) => {
  const date = normalizeDateValue(value);
  if (!date) return "";
  return format(date, "yyyy/MM/dd");
};

export const normalizeTimeValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const meridiemMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])$/);
    if (meridiemMatch) {
      const [, rawHours, minutes, meridiem] = meridiemMatch;
      let hours = Number(rawHours);
      if (Number.isNaN(hours)) return "";
      const isPm = meridiem.toLowerCase() === "pm";
      if (hours === 12) {
        hours = isPm ? 12 : 0;
      } else if (isPm) {
        hours += 12;
      }
      return `${String(hours).padStart(2, "0")}:${minutes}`;
    }
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) return trimmed.slice(0, 5);
    if (/^\d{1,2}:\d{2}$/.test(trimmed)) return trimmed;
    return "";
  }
  if (typeof value === "object") {
    return normalizeTimeValue(value?.value ?? value?.label ?? "");
  }
  return "";
};

export const buildDateTimeString = (dateValue, timeValue) => {
  const date = normalizeDateValue(dateValue);
  if (!date) return "";
  const day = format(date, "yyyy-MM-dd");
  const time = normalizeTimeValue(timeValue);
  if (!time) return `${day} 00:00:00`;
  const [hh, mm] = time.split(":");
  return `${day} ${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
};

export const normalizeServerFiles = (files = []) =>
  (Array.isArray(files) ? files : []).filter(Boolean).map((file, index) => ({
    ...file,
    uuid:
      file?.uuid ??
      String(file?.id ?? file?.archivo ?? file?.file ?? `server-file-${index}`),
    estado: file?.estado ?? "bd",
    archivo: file?.archivo ?? file?.file ?? file?.name ?? "archivo",
  }));

export const normalizeNewFiles = (files = []) =>
  (Array.isArray(files) ? files : []).filter(Boolean);

export const buildManagedFiles = ({ serverFiles = [], newFiles = [] } = {}) => ({
  serverFiles: normalizeServerFiles(serverFiles),
  newFiles: normalizeNewFiles(newFiles),
});

export const pickManagedFileDeletes = (serverFiles = []) =>
  normalizeServerFiles(serverFiles)
    .filter((file) => file?.estado === "eliminar")
    .map((file) => ({
      id: file?.id,
      file: file?.archivo,
    }))
    .filter((file) => !isNil(file.id) && !isBlank(file.file));
